import * as React from 'react'

import { clsx } from 'clsx'

import { BufferIcon, Heading, Link, Tag, Text } from '@buffer-mono/popcorn'
import type { HeadingProps, TextProps } from '@buffer-mono/popcorn'

import styles from './TemplateDetails.module.css'

import type { JSX } from 'react'

type TemplateDetailsData = {
  body: string
  /** RGB components as comma-separated string, e.g. "100, 149, 237" */
  color: string
  description: string
  emoji?: string
  goal: string
  title: string
  type: string
}

const TemplateDetailsContext = React.createContext<TemplateDetailsData | null>(
  null,
)

function useTemplateDetailsContext(): TemplateDetailsData {
  const context = React.useContext(TemplateDetailsContext)
  if (!context) {
    throw new Error(
      'TemplateDetails sub-components must be used within TemplateDetails',
    )
  }
  return context
}

/**
 * Compound component for rendering a template's expanded detail view.
 * Distributes template data to sub-components via context.
 *
 * @example
 * ```tsx
 * <TemplateDetails template={template}>
 *   <TemplateDetails.IconSwatch>
 *     <TemplateDetails.Icon />
 *   </TemplateDetails.IconSwatch>
 *   <TemplateDetails.Title />
 *   <TemplateDetails.Description />
 *   <TemplateDetails.Body>
 *     <TemplateDetails.Actions>
 *       <IconButton onClick={onCopy} label="Copy template">
 *         <CopyIcon />
 *       </IconButton>
 *       <Button onClick={onUseTemplate}>Use Template</Button>
 *     </TemplateDetails.Actions>
 *   </TemplateDetails.Body>
 *   <TemplateDetails.Metadata />
 * </TemplateDetails>
 * ```
 */
type RootProps = React.ComponentPropsWithoutRef<'section'> & {
  /**
   * Template data distributed to sub-components via context
   */
  template: TemplateDetailsData
}

const Root = ({
  template,
  className,
  style,
  children,
  ...rest
}: RootProps): JSX.Element => {
  const mergedStyle: React.CSSProperties & Record<`--${string}`, string> = {
    '--template-color': template.color,
    ...style,
  }

  return (
    <TemplateDetailsContext.Provider value={template}>
      <section
        style={mergedStyle}
        className={clsx(styles.root, className)}
        aria-label={template.title}
        {...rest}
      >
        {children}
      </section>
    </TemplateDetailsContext.Provider>
  )
}

Root.displayName = 'TemplateDetails'

/**
 * TemplateDetails IconSwatch
 *
 * Bordered box with a tinted background derived from the template color.
 * Accepts children so any content (emoji, icon, etc.) can be placed inside.
 */
type IconSwatchProps = React.ComponentPropsWithoutRef<'div'>

const IconSwatch = ({
  className,
  children,
  ...rest
}: IconSwatchProps): JSX.Element => {
  useTemplateDetailsContext()

  return (
    <div className={clsx(styles.icon, className)} {...rest}>
      {children}
    </div>
  )
}

IconSwatch.displayName = 'TemplateDetails.IconSwatch'

/**
 * TemplateDetails Icon
 *
 * Renders the template emoji (or a default) as an inline span.
 */
type IconProps = Omit<React.ComponentPropsWithoutRef<'span'>, 'children'> & {
  size?: TextProps['size']
}

const Icon = ({ size = 'lg', className, ...rest }: IconProps): JSX.Element => {
  const template = useTemplateDetailsContext()

  return (
    <span
      aria-hidden={true}
      className={clsx(styles[size], className)}
      {...rest}
    >
      {template.emoji || '📒'}
    </span>
  )
}

Icon.displayName = 'TemplateDetails.Icon'

/**
 * TemplateDetails Title
 */
type TitleProps = Omit<HeadingProps, 'children' | 'as'> & {
  /** Heading level, so the title fits the hierarchy around it. */
  as?: Extract<HeadingProps['as'], 'h2' | 'h3' | 'h4'>
}

const Title = ({
  size = 'lg',
  className,
  as = 'h2',
  ...rest
}: TitleProps): JSX.Element => {
  const template = useTemplateDetailsContext()

  return (
    <Heading
      as={as}
      size={size}
      className={clsx(styles.title, className)}
      {...rest}
    >
      {template.title}
    </Heading>
  )
}

Title.displayName = 'TemplateDetails.Title'

/**
 * TemplateDetails Description
 */
type DescriptionProps = Omit<TextProps, 'children' | 'as'> & {
  className?: string
}

const Description = ({
  size = 'lg',
  color = 'subtle',
  className,
  ...rest
}: DescriptionProps): JSX.Element => {
  const template = useTemplateDetailsContext()

  return (
    <Text
      color={color}
      size={size}
      className={clsx(styles.description, className)}
      {...rest}
    >
      {template.description}
    </Text>
  )
}

Description.displayName = 'TemplateDetails.Description'

/**
 * TemplateDetails Body
 */
type BodyProps = React.ComponentPropsWithoutRef<'div'> & {
  size?: TextProps['size']
}

const Body = ({
  size = 'lg',
  className,
  children,
  ...rest
}: BodyProps): JSX.Element => {
  const template = useTemplateDetailsContext()

  return (
    <div className={clsx(styles.body, className)} {...rest}>
      <Text size={size}>{template.body}</Text>
      {children}
    </div>
  )
}

Body.displayName = 'TemplateDetails.Body'

/**
 * TemplateDetails Actions
 */
type ActionsProps = React.ComponentPropsWithoutRef<'div'> & {
  children: React.ReactNode
}

const Actions = ({
  className,
  children,
  ...rest
}: ActionsProps): JSX.Element => {
  useTemplateDetailsContext()

  return (
    <div className={clsx(styles.actions, className)} {...rest}>
      {children}
    </div>
  )
}

Actions.displayName = 'TemplateDetails.Actions'

/**
 * TemplateDetails Metadata
 *
 * Attribution and categorization block. Renders "By Buffer" branding (or a
 * custom `byline`) with goal and type tags from context. No children.
 */
type MetadataProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> & {
  byline?: React.ReactNode
  showTags?: boolean
}

const Metadata = ({
  className,
  byline,
  showTags = true,
  ...rest
}: MetadataProps): JSX.Element => {
  const template = useTemplateDetailsContext()

  return (
    <div className={clsx(styles.metadata, className)} {...rest}>
      {byline ?? (
        <Text color="subtle" icon={<BufferIcon color="default" />}>
          By&nbsp;
          <Link
            external
            href="https://buffer.com"
            style={{ color: 'var(--color-text-subtle)' }}
          >
            Buffer
          </Link>
        </Text>
      )}
      {showTags && (
        <>
          <Text size="lg" color="subtle">
            &nbsp;•&nbsp;
          </Text>
          <div className={styles.metadataTags}>
            <Tag className={styles.tag}>{template.goal}</Tag>
            <Tag className={styles.tag}>{template.type}</Tag>
          </div>
        </>
      )}
    </div>
  )
}

Metadata.displayName = 'TemplateDetails.Metadata'

export const TemplateDetails = Object.assign(Root, {
  Actions,
  Body,
  Description,
  Icon,
  IconSwatch,
  Metadata,
  Title,
})

export type {
  ActionsProps as TemplateDetailsActionsProps,
  BodyProps as TemplateDetailsBodyProps,
  DescriptionProps as TemplateDetailsDescriptionProps,
  IconProps as TemplateDetailsIconProps,
  IconSwatchProps as TemplateDetailsIconSwatchProps,
  MetadataProps as TemplateDetailsMetadataProps,
  RootProps as TemplateDetailsProps,
  TemplateDetailsData,
  TitleProps as TemplateDetailsTitleProps,
}
